import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { RoleContext } from '@/app/api/common';
import { store, useAppSelector } from '@/app/store';
import { checkNoError } from '@/features/channels/channelsSlice';
import { copyLoginDescriptor, getNewLogin, getLoginFromStruct, LoginDescriptor } from '@/features/logins/loginsSlice';

import Button from '@/components/Common/Button';
import Dropdown, { FieldInfo } from '@/components/Common/Dropdown';
import CategorizedPrivilegeDropdown from '@/components/Common/CategorizedPrivilegeDropdown';
import FlexRow, { FlexRowProps } from '@/components/Common/FlexRow';
import Input, { TextArea } from '@/components/Common/Input';
import MenuItem from '@/components/Common/MenuItem';
import { ButtonsModal } from '@/components/Common/Modals';
import Tabs from '@/components/Common/Tabs';

import { CatHeader } from '@/pages/Projects/Project';

import { getUserPageUrl } from './Users';

const User = () => {
  const { t } = useTranslation();

  const { user } = useParams();
  const navigate = useNavigate();
  const role = React.useContext(RoleContext);

  const logins = useAppSelector((state) => state.logins);
  const canEditUsers = role.admin || role?.privilages?.includes("ChangeGlobalUsers");
  
  const [login, setLogin] = React.useState(null as LoginDescriptor | null);
  const [changes, setChanges] = React.useState(false);
  const [triedSave, SetTriedSave] = React.useState(false);
  const [confirm2FA, setConfirm2FA] = React.useState<null | 'enable' | 'disable'>(null);
  // Server-wide 2FA capability (from the current session's /login.json
  // descriptor). The admin two-factor controls only show when the backend
  // reports supports_2fa === true; false/absent → hidden.
  const show2FAAdmin = logins.supports2FA;
  // Anti-autofill guard. Chrome's password manager treats the username +
  // type=password pair as a login form and autofills the signed-in admin's
  // own saved credentials into this new-user form. Browsers skip read-only
  // fields for autofill, so the credential inputs start read-only and flip
  // editable the moment the admin focuses one. Reset whenever the form opens.
  const [credFocused, setCredFocused] = React.useState(false);
  const handleSave = React.useRef(() => { return; });
  
  const caption =	t('users:edit_user');
  console.log("User/login=", login);
  
  useEffect(() => {
    setLogin(null);
    setChanges(false);
    setCredFocused(false);

    const lgn = user &&
    			user === "__new__"
    			?	getNewLogin()
    			:	(	user
    					?	getLoginFromStruct(user, logins.logins)
    					:	null
    				);
    if (!lgn)
      return;

    // Reset password to blank!
    lgn.password = "";

    setLogin(lgn);
  }, [logins.logins, user]);
  
  const usernameInvalid = React.useMemo(() => {
    return	login &&
    		(	(	!login.is_new &&
    				login.username.trim() == ''
    			) ||
    			!login.username.match(/^[A-Za-z0-9ŽžÀ-ÿ_\.]+$/)
    			//!login.username.match(/^[a-zA-Z0-9äöüÄÖÜàéèÀÉÈâêôîÂÊÔÎçß_\.]+$/)
    		)
    		?	true
    		:	false;
  }, [login]);
  
  const passwordInvalid = React.useMemo(() => {
    return	login &&
    		login.is_new &&
			login.password.length < 4
			?	true
			:	false;
  }, [login]);
  
  const roleInvalid = React.useMemo(() => {
    return	login &&
    		(	!login.role ||
    			login.role.trim() == ''
    		)
			?	true
			:	false;
  }, [login]);
  
  /*const nameInvalid = React.useMemo(() => {
    return	login &&
    		(	!login.name ||
    			login.name.trim() == ''
    		)
			?	true
			:	false;
  }, [login]);*/
  
  const maxProjectsInvalid = React.useMemo(() => {
    return	login &&
    		(	login.max_projects === undefined ||
    			login.max_projects < 0
    		)
			?	true
			:	false;
  }, [login]);
  
  const ownerInvalid = React.useMemo(() => {
    return	login &&
    		login.role != "admin" &&
    		login.owner &&
    		login.owner.trim() ==''
 			?	true
			:	false;
 }, [login]);
  
  const emailInvalid = React.useMemo(() => {
    return	login &&
    		login.email &&
    		!(/^\S+@\S+\.\S+$/.test(login.email))
 			?	true
			:	false;
 }, [login]);
  
  const ownerOpts = React.useMemo(() => {
    return logins.logins.descriptors.map(
      (user: LoginDescriptor): FieldInfo => (
        {
          value:	user.username,
          desc:		`${user.username} (${user.name})`,
        }
      )
    );
  }, [logins.logins]);
  
  const roleOpts = React.useMemo(() => {
    return [
      {	value:	"admin",		desc:	t("users:roles:admin") },
      {	value:	"manager",		desc:	t("users:roles:manager") },
      //{	value:	"api",			desc:	t("users:roles:api") },
      //{	value:	"reseller",		desc:	t("users:roles:reseller") },
    ];
  }, [t]);
  
  const updateLogin = React.useCallback((lgn: LoginDescriptor) => {
    setChanges(true);
    setLogin(lgn);
  }, []);
  
  const updateUsername = React.useCallback((user: string) => {
    if (!login)
      return;
    
    let lgn = copyLoginDescriptor(login);
    lgn.username = user;
    
    updateLogin(lgn);
  }, [login]);
  
  const updatePassword = React.useCallback((pass: string) => {
    if (!login)
      return;
    
    let lgn = copyLoginDescriptor(login);
    lgn.password = pass;
    
    updateLogin(lgn);
  }, [login]);
  
  const updateRole = React.useCallback((role: string) => {
    if (!login)
      return;
    
    let lgn = copyLoginDescriptor(login);
    lgn.role = role;
    
    updateLogin(lgn);
  }, [login]);
  
  const updateName = React.useCallback((name: string) => {
    if (!login)
      return;
    
    let lgn = copyLoginDescriptor(login);
    lgn.name = name;
    
    updateLogin(lgn);
  }, [login]);
  
  const updateOwner = React.useCallback((owner: string) => {
    if (!login)
      return;
    
    let lgn = copyLoginDescriptor(login);
    lgn.owner = owner;
    
    updateLogin(lgn);
  }, [login]);
  
  const updateMaxProjects = React.useCallback((str: string) => {
    if (!login)
      return;
    
    if (str == "")
      str = "0";
    
    const val = parseInt(str);
    
    let lgn = copyLoginDescriptor(login);
    lgn.max_projects = val;
    
    updateLogin(lgn);
  }, [login]);
  
  const updateEmail = React.useCallback((val: string) => {
    if (!login)
      return;
    
    let lgn = copyLoginDescriptor(login);
    lgn.email = val;
    
    updateLogin(lgn);
  }, [login]);
 const updatePrevilage = React.useCallback((val: string[]) => {
    if (!login)
      return;

    const lgn = copyLoginDescriptor(login);
    lgn.privileges = val;

    updateLogin(lgn);
  }, [login, logins, updateLogin]); 
  handleSave.current = async () => {
    if (!login)
      return;
    
    if (	usernameInvalid ||
			passwordInvalid ||
			roleInvalid ||
			//maxProjectsInvalid ||
			emailInvalid
			//ownerInvalid
       ) {
      SetTriedSave(true);
      return;
    }
    
    const wasNew = login && login.is_new;
    
    const mess = await store.setLogin(login);
    console.log("User/handleSave/mess=", mess);
    
    if (!checkNoError(mess))
      return;
    
    SetTriedSave(false);
    
    navigate(getUserPageUrl(""));
    /*navigate(
      getUserPageUrl(
      	mess &&
        wasNew
        ?	login.username
        :	""
      )
    );*/
  };
  const isNew = login && login.is_new;
  const loaded = login ? true : false;
  const isAdmin =	logins.login && logins.login.role == 'admin';
  
  return (
  <React.Fragment>
    <div className="px-[32px] w-full h-[calc(100vh-150px)] overflow-y-auto">
      <div className="flex flex-col items-stretch py-0 pb-[100px] gap-[16px] w-full">
		<CatHeader caption={isNew ? 'users:new_user' : 'users:edit_user'} />
		<Input label={t('users:username:caption') + '*'}
		       placeholder={t('users:username:placeholder')}
		       isLoaded={loaded}
		       isDisabled={!isNew}
		       isReadOnly={!isNew || !credFocused}
		       isInvalid={triedSave && usernameInvalid}
		       errorMessage={t('users:username:invalid')}
		       value={login ? login.username : ''}
		       autoComplete="off"
		       onFocus={() => setCredFocused(true)}
		       onValueChange={updateUsername} />
		<Input label={t('users:password:caption') + '*'}
		       placeholder={t("users:password:placeholder" + (isNew ? "_new" : ""))}
		       isLoaded={loaded}
		       type="password"
		       isReadOnly={!credFocused}
		       isInvalid={triedSave && passwordInvalid}
		       errorMessage={t('users:password:invalid')}
		       value={login ? login.password : ''}
		       autoComplete="new-password"
		       onFocus={() => setCredFocused(true)}
		       onValueChange={updatePassword} />
        <Dropdown label={t('users:role:caption') + '*'}
		          isLoaded={loaded}
		          value={login ? login.role : ""}
		          placeholder={t('users:role:placeholder')}
		          options={roleOpts}
		          isInvalid={triedSave && roleInvalid}
		          errorMessage={t('users:role:invalid')}
		          setValue={updateRole}>
		  <>
		  </>
		</Dropdown>
		<TextArea value={login ? login.name : ""}
		          isLoaded={loaded}
		          onValueChange={updateName}
		          label={t("users:name:caption")}
		          placeholder={t("users:name:placeholder")} />
	  {/*
		<Dropdown label={t('users:owner:caption') + '*'}
		          isLoaded={loaded}
		          value={login ? login.owner : ""}
		          placeholder={t('users:owner:placeholder')}
		          options={ownerOpts}
		          isInvalid={triedSave && ownerInvalid}
		          errorMessage={t('users:owner:invalid')}
		          setValue={updateOwner}>
		  <>
		  </>
		</Dropdown>
	  */}
		<Input label={t('users:email:caption') + '*'}
		       placeholder={t('users:email:placeholder')}
		       isLoaded={loaded}
		       isInvalid={triedSave && emailInvalid}
		       errorMessage={t('users:email:invalid')}
		       value={login && login.email ? login.email : ""}
		       onValueChange={updateEmail} />
    {logins?.login?.role == 'admin' && login?.role != 'admin' &&
            <CategorizedPrivilegeDropdown
              label={t('users:privileges:caption')}
              placeholder={t('users:privileges:select_the_privileges')}
              privileges={logins?.logins?.privileges || []}
              selectedPrivileges={login?.privileges || []}
              onSelectionChange={updatePrevilage}
              errorMessage={t('users:privileges:invalid')}
            />
    }

    { /* Two-factor authentication admin section. Only admins (or users
         with ChangeGlobalUsers) see this. Hidden on the "new user" form
         because the backing record must exist server-side first. */ }
    { show2FAAdmin && canEditUsers && !isNew && login &&
      <FlexRow direction="column"
               flex={0}
               className="gap-[8px] mt-[8px] p-[12px] border border-[--Mid-Grey] rounded-[6px] bg-[--Light-Grey]">
        <FlexRow flex={0} className="gap-[8px] items-center">
          <span className="material-icons text-[--Primetime-main] text-[18px]">security</span>
          <span className="r14 font-bold">Two-factor authentication</span>
        </FlexRow>
        <span className="r12 text-[--Dark-Grey]">
          { login.totp_disabled
              ? "2FA is currently DISABLED for this user. They can sign in with password alone — no authenticator code required. Re-enable to force them through fresh enrollment on next login."
              : login.totp_enrolled
                ? "2FA is ENABLED and the user has already enrolled at least one authenticator device. Disabling here will wipe their devices and recovery codes, and let them sign in with password alone."
                : "2FA is required for this user (default). On next login they will be walked through the QR-scan enrollment flow. Disabling here exempts them from that requirement."
          }
        </span>
        <FlexRow flex={0} className="gap-[8px]">
          { login.totp_disabled
              ? <Button caption="Enable 2FA (force re-enrollment)"
                        icon="lock"
                        onClick={() => setConfirm2FA('enable')} />
              : <Button caption="Disable 2FA for this user"
                        icon="lock_open"
                        onClick={() => setConfirm2FA('disable')} />
          }
        </FlexRow>
      </FlexRow>
    }
    { show2FAAdmin && confirm2FA && login &&
      <ButtonsModal title={confirm2FA === 'disable' ? 'Disable 2FA?' : 'Enable 2FA?'}
                    message={confirm2FA === 'disable'
                      ? `This will wipe all authenticator devices and recovery codes for "${login.username}". They'll be able to sign in with just their password until you re-enable 2FA.`
                      : `This will require "${login.username}" to set up a fresh authenticator on their next login. Any old devices and recovery codes will be wiped.`}
                    onResult={async (_obj, result) => {
                      const target = confirm2FA;
                      setConfirm2FA(null);
                      if (result !== 'yes' || !login.username) return;
                      await store.adminSetTotpDisabled(login.username, target === 'disable');
                    }}>
        <></>
      </ButtonsModal>
    }
	  {/*
		<Input label={t('users:max_projects:caption') + '*'}
		       placeholder={t('users:max_projects:placeholder')}
		       isLoaded={loaded}
		       isInvalid={triedSave && maxProjectsInvalid}
		       errorMessage={t('users:max_projects:invalid')}
		       value={login && login.max_projects ? login.max_projects.toString() : "0"}
		       onValueChange={updateMaxProjects} />
	  */}
	  </div>
    </div>
    <FlexRow className="absolute bottom-0 p-[32px] bg-white z-10 justify-end gap-[16px]">
      <Button className="uppercase"
              caption={t('users:cancel')}
              onClick={() => navigate(getUserPageUrl(""))}
              layout="white" />
      {canEditUsers &&
      <Button className="uppercase"
              caption={t('users:save')}
              icon="save"
              isDisabled={
              	!changes ||
              	(	triedSave &&
              		(	usernameInvalid ||
						passwordInvalid ||
						roleInvalid
						//maxProjectsInvalid ||
						//nameInvalid ||
						//ownerInvalid
					)
				)
			  }
              onClick={() => handleSave.current()} />
      }
    </FlexRow>
  </React.Fragment>
  );
};

export default User;


